import { fabric } from 'fabric';
import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { extractBase64FromUrl, downloadBlob, updateCFBucketUrls } from 'utils';
import {
  extendControls,
  initCustomMethods,
  initListeners,
  handleAlignmentGuides,
  cancelAlignmentGuides,
  processDynamicLines,
} from 'utils/workspace';
import CreationService from 'services/CreationService';
import TemplatesService from 'services/TemplatesService';
import { UserTypeEnum, CanvasToolbarEnum } from 'enums';
import { setLoadError, showSnackbar } from './generalSlice';
import CanvasHandler from 'utils/workspace/svgDownload';
import { downloadFolder } from 'utils/workspace/svgDownload/downloadFolder';
import { getImageBase64Url } from 'utils/workspace';
import { logCanvasError /*, trackCanvasMetric */ } from 'utils/logCanvasAnalytics'; // trackCanvasMetric removed: @datadog/browser-rum package removed
// import { serializeCanvas } from 'utils/fabricSerializer'; AD: DO NOT REMOVE THIS LINE, may use this later
import { getTemplateLabelBySize } from 'utils/workspace/templateSizes';
import { getShowCommentsFromLocalStorage } from 'features/auth/utils';
import { create } from 'jsondiffpatch';
import { debounce } from 'lodash';

const MAX_UNDO_STACKS = 30;
let isTrial = true;
let userInfo = null;

export const CANVAS_DIMENSIONS = {
  width: 2500,
  height: 2500,
};

const LEGACY_CANVAS_DIMENSIONS = {
  width: 1500,
  height: 1500,
};

const LEGACY_CANVAS_DIFF_TOP = (CANVAS_DIMENSIONS.width - LEGACY_CANVAS_DIMENSIONS.width) / 2;
const LEGACY_CANVAS_DIFF_LEFT = (CANVAS_DIMENSIONS.height - LEGACY_CANVAS_DIMENSIONS.height) / 2;

export let instance = null;

const withoutAutoRender = (fn) => {
  const prev = instance.renderOnAddRemove;
  const canToggle = 'renderOnAddRemove' in instance && prev != null;
  if (canToggle) instance.renderOnAddRemove = false;
  fn();
  if (canToggle) instance.renderOnAddRemove = prev;
};

const jdp = create({
  objectHash: (obj) => obj.id || JSON.stringify(obj),
  arrays: {
    detectMove: true,
    includeValueOnMove: false,
  },
});

const initialState = {
  saving: false,
  lastSaveDate: new Date().getTime(),
  activeToolbar: null,
  upgradeOfferBannerOpen: false,
  countdownTrialBannerOpen: false,
  downloadProgressBarOpen: {
    downloadingFile: false,
    downloadDone: false,
    downloadFailed: false,
  },
  upgradeTopBarTrialOfferBannerOpen: false,
  toolbarPosition: {
    x: 0,
    y: 0,
  },
  currentDimensions: {
    width: 0,
    height: 0,
  },
  grid: {
    open: false,
    size: 50,
  },
  zoom: 1,
  alignmentGuides: false,
  currentState: null,
  redoStack: [],
  undoStack: [],
  undoBusy: false,
  user: null,
  isCanvasEditable: true,
  selectedTemplate: {
    id: null,
    width: null,
    height: null,
    isLegacy: false,
  },
  isInputFocused: false,
  keepSidePanelOpen: true,
  selectedChart: {
    type: null,
    hasErrorInput: false,
    left: 0,
    top: 0,
  },
  isCanvasLoading: false,
};

const historicSaveAction = createAsyncThunk('canvas/historicSave', async (_, { getState }) => {
  const state = getState().canvas;

  if (!state.isCanvasEditable) {
    return null;
  }

  const newState = instance.toJSON();
  const currentState = state.currentState ? JSON.parse(state.currentState) : null;

  // Skip if no previous state exists (first save)
  if (!currentState) {
    return { currentState: JSON.stringify(newState), undoEntry: null };
  }

  // If the initial baseline did not have a backgroundImage but the current state does,
  // refresh the baseline to the new state and skip pushing an undo entry.
  // This prevents undoing back to a state with no background board.
  if (!currentState.backgroundImage && newState.backgroundImage) {
    return { currentState: JSON.stringify(newState), undoEntry: null };
  }

  // Calculate delta
  const delta = jdp.diff(currentState, newState);

  // Skip if no changes detected
  if (!delta) {
    return null;
  }

  // Calculate inverse delta for undo
  const inverse = jdp.diff(newState, currentState);

  return {
    currentState: JSON.stringify(newState),
    undoEntry: { delta, inverse },
  };
});

/**
 * Prevents overlapping/rapid successive calls: only fires twice every second.
 * Uses `leading: false, trailing: true` so the save always reflects the latest state.
 */
const _debouncedDispatchHistoricSave = debounce((dispatch) => dispatch(historicSaveAction()), 500, {
  leading: false,
  trailing: true,
});

/**
 * Debounced historicSave thunk. Drop-in replacement for the old historicSave.
 */
export const historicSave = () => (dispatch) => {
  _debouncedDispatchHistoricSave(dispatch);
};

export const canvasSlice = createSlice({
  name: 'canvas',
  initialState: { ...initialState },
  reducers: {
    reset: () => {
      return { ...initialState };
    },
    setSaving: (state) => {
      state.saving = true;
    },
    setNotSaving: (state) => {
      state.saving = false;
    },
    updateLastSaveDate: (state) => {
      state.lastSaveDate = new Date().getTime();
    },
    openToolbar: (state, action) => {
      state.activeToolbar = action.payload;
    },
    closeToolbar: (state) => {
      state.activeToolbar = null;
    },
    openUpgradeOfferBanner: (state) => {
      state.upgradeOfferBannerOpen = true;
    },
    closeUpgradeOfferBanner: (state) => {
      state.upgradeOfferBannerOpen = false;
    },
    openCountdownTrialBanner: (state) => {
      state.countdownTrialBannerOpen = true;
    },
    closeCountdownTrialBanner: (state) => {
      state.countdownTrialBannerOpen = false;
    },
    openDownloadProgressBar: (state, action) => {
      state.downloadProgressBarOpen = action.payload;
    },
    closeDownloadProgressBar: (state) => {
      state.downloadProgressBarOpen = {
        downloadingFile: false,
        downloadDone: false,
        downloadFailed: false,
      };
    },
    openTopBarTrialOfferBanner: (state) => {
      state.upgradeTopBarTrialOfferBannerOpen = true;
    },
    closeTopBarTrialOfferBanner: (state) => {
      state.upgradeTopBarTrialOfferBannerOpen = false;
    },
    setCurrentDimensions: (state, action) => {
      state.currentDimensions = action.payload;
    },
    setUser: (state, action) => {
      state.user = action.payload;
    },
    setIsCanvasEditable: (state, action) => {
      state.isCanvasEditable = action.payload;
    },
    setIsCanvasLoading: (state, action) => {
      state.isCanvasLoading = Boolean(action.payload);
    },
    init: (state, action) => {
      const { selector, user } = action.payload;
      initCustomMethods();

      instance = new fabric.Canvas(document.querySelector(selector), {
        width: CANVAS_DIMENSIONS.width,
        height: CANVAS_DIMENSIONS.height,
        isDrawingMode: false,
        renderOnAddRemove: true,
        backgroundColor: 'transparent',
        preserveObjectStacking: true,
      });

      instance.wrapperEl.style.margin = 'auto';

      initListeners(instance, user);
      extendControls(instance);
    },
    end: (state, action) => {
      if (instance) {
        instance.off();
        if (typeof instance.clear === 'function') {
          instance.clear();
        }
        if (typeof instance.dispose === 'function') {
          instance.dispose();
        }
      }
    },
    scrollToCenter: (state, action) => {
      const canvasArea = document.getElementById('canvas-area');
      const left = (instance.getWidth() - canvasArea.offsetWidth) / 2;
      const top = (instance.getHeight() - canvasArea.offsetHeight) / 2;

      canvasArea.scrollTo(left, top);
    },
    setCanvasBackground: (state, action) => {
      if (!state.isCanvasEditable) {
        return;
      }
      const { width, height, fill } = { ...action.payload };

      const backgroundFromSrc = instance.backgroundImage?.src
        ? handleLegacyBackground(instance.backgroundImage.src)
        : null;

      const currentFill =
        fill || backgroundFromSrc || instance.backgroundImage?.fill || 'rgb(255, 255, 255)';

      applyBackground(width, height, currentFill);
    },
    addPillObject: (state, action) => {
      if (!state.isCanvasEditable) {
        return;
      }

      const pill = new fabric.Path('');

      const width = 300;
      const height = 150;

      const pathString = `M -${width / 4} -${height / 2} C -${-width / 2} -${height / 2} ,-${
        -width / 2
      } ${height / 2}, -${width / 4} ${height / 2} L ${width / 4} ${height / 2} C ${width / 2} ${
        height / 2
      }, ${width / 2} -${height / 2}, ${width / 4} -${height / 2} Z`;
      const newPath = new fabric.Path(pathString);

      pill.set({
        path: newPath.path,
        width: width,
        height: height,
      });
      handleDynamicShape(pill);
    },
    addTrapezoidObject: (state, action) => {
      if (!state.isCanvasEditable) {
        return;
      }

      const object = new fabric.Polygon(
        [
          { x: 0, y: 0 },
          { x: 0, y: 0 },
          { x: 0, y: 0 },
          { x: 0, y: 0 },
        ],
        {
          fill: '#000',
        },
      );

      const width = 300;
      const height = 300;

      object.points[0].x = width / 4 + 0;
      object.points[0].y = 0;
      object.points[1].x = width - width / 4;
      object.points[1].y = 0;
      object.points[2].x = width;
      object.points[2].y = height;
      object.points[3].x = 0;
      object.points[3].y = height;
      object._setPositionDimensions({});
      handleDynamicShape(object);
    },
    addRectangleObject: (state, action) => {
      if (!state.isCanvasEditable) {
        return;
      }

      const rect = new fabric.Path('');

      let width = 300;
      let height = 300;

      // Fabric Rect is not saving Rx on clone, so Path is used instead
      const pathString = action.payload.isRounded
        ? `M${-width / 2 + 10} ${-height / 2}
            H ${width / 2 - 10}
            Q ${width / 2} ${-height / 2}, ${width / 2} ${-height / 2 + 10}
            V ${height / 2 - 10}
            Q ${width / 2} ${height / 2}, ${width / 2 - 10} ${height / 2}
            H ${-width / 2 + 10}
            Q ${-width / 2} ${height / 2}, ${-width / 2} ${height / 2 - 10}
            V ${-height / 2 + 10}
            Q ${-width / 2} ${-height / 2}, ${-width / 2 + 10} ${-height / 2} Z`
        : `M${-width / 2} ${-height / 2} h${width} v${height} h${-width}Z`;

      const newPath = new fabric.Path(pathString);

      rect.set({
        path: newPath.path,
        width: width,
        height: height,
        left: 0,
        top: 0,
      });
      handleDynamicShape(rect);
    },
    addPolygonObject: (state, action) => {
      if (!state.isCanvasEditable) {
        return;
      }

      const sides = action.payload.sides;
      const object = new fabric.Polygon([], {
        fill: '#000',
        points: [],
      });

      const mx = 300;

      object.points.length = 0;

      let radius = mx / 2;

      // This code generates the points of a polygon with the given number of sides
      const points = [];
      for (let i = 0; i < sides; i++) {
        // The angle of each vertex is 2 * PI / number of sides
        const angle = (i * 2 * Math.PI) / sides + (sides % 2 === 0 ? 0 : -Math.PI / 2);
        // The x and y coordinates of each vertex are the radius of the circle times the cosine and sine of the angle
        points.push({
          x: radius * Math.cos(angle) + radius,
          y: radius * Math.sin(angle) + radius,
        });
      }
      object.set('points', points);
      object._setPositionDimensions({});
      handleDynamicShape(object);
    },
    addTriangleObject: (state, action) => {
      if (!state.isCanvasEditable) {
        return;
      }

      if (action.payload.isRounded) {
        const object = new fabric.Polygon(
          [
            { x: 0, y: 0 },
            { x: 0, y: 0 },
            { x: 0, y: 0 },
          ],
          {
            fill: '#000',
            strokeLineJoin: 'round',
            strokeWidth: 20,
            stroke: '#000',
          },
        );

        let height = 300;
        let width = 300;

        object.set('points', [
          { x: width / 2, y: 0 },
          { x: 0, y: height },
          { x: width, y: height },
        ]);
        object._setPositionDimensions({});
        handleDynamicShape(object);
      }

      if (action.payload.right) {
        const object = new fabric.Polygon([], {
          fill: '#000',
        });

        const mx = 300;
        const my = 300;

        object.set('points', [
          { x: 0, y: 0 },
          { x: mx, y: my },
          { x: 0, y: my },
        ]);
        object._setPositionDimensions({});
        handleDynamicShape(object);
      }
    },
    addLineObject: (state, action) => {
      if (!state.isCanvasEditable) {
        return;
      }

      let object;
      if (action.payload.isRounded) {
        const curve = new fabric.DynamicLine('M0 0h0s0 0 0 0v0');

        const width = 100;
        const height = 100;
        const stroke = 5;

        const arc = 40;
        const pathString = `M${(width * -1) / 2} ${((height - stroke / 2) * -1) / 2} h${
          width - arc
        } s${arc} 0 ${arc} ${arc} v${height - arc}`;
        const newPath = new fabric.DynamicLine(pathString);

        curve.set({
          path: newPath.path,
          width: width,
          height: height,
          strokeWidth: stroke,
          stroke: '#000',
          fill: 'transparent',
        });
        handleDynamicLine(curve);
      } else if (action.payload.rightAngle) {
        object = new fabric.DynamicLine('M 0 0 L 0 0 L 0 0', {
          stroke: '#000',
          strokeWidth: 4,
          fill: 'transparent',
          leftPointer: action.payload.leftPointer,
          rightPointer: action.payload.rightPointer,
        });

        object.width = 100;
        object.height = 100;
        const hW = object.width / 2;
        const hH = object.height / 2;

        object.path[0][1] = 0 - hW;
        object.path[0][2] = 0 - hH;

        object.path[1][1] = object.width - hW;
        object.path[1][2] = object.path[0][2];
        object.path[2][1] = object.width - hW;
        object.path[2][2] = object.height - hH;
        handleDynamicLine(object);
      }
    },
    addWavyLineObject: (state, action) => {
      if (!state.isCanvasEditable) {
        return;
      }
      const w = 400;
      const h = 100;

      const pathString =
        `M ${-w / 2} 0 ` +
        `C ${-w / 2} 0 ${-(w / 2) * 0.95} ${h / 2} ${-(w / 2) * 0.75} ${h / 2} ` +
        `C ${-w / 4} ${h / 2} ${-w / 4} ${-h / 2} ${-w / 8} ${-h / 2} ` +
        `C 0 ${-h / 2} 0 ${h / 2} ${w / 8} ${h / 2} ` +
        `C ${w / 4} ${h / 2} ${w / 4} ${-h / 2} ${(w / 8) * 3} ${-h / 2} ` +
        `C ${(w / 2) * 0.9} ${-h / 2} ${(w / 2) * 0.9} ${h / 5} ${w / 2} 0`;

      let object = new fabric.DynamicLine(pathString, {
        stroke: '#000',
        strokeWidth: 4,
        fill: 'transparent',
        leftPointer: action.payload.leftPointer,
        rightPointer: action.payload.rightPointer,
      });

      object.set({
        width: w,
        height: h,
      });

      handleDynamicLine(object);
    },
    addStraightLineObject: (state, action) => {
      if (!state.isCanvasEditable) {
        return;
      }

      let object;

      object = new fabric.DynamicLine('M 0 0 L 0, 0', {
        stroke: '#000',
        strokeWidth: 4,
        fill: 'transparent',
        leftPointer: action.payload.leftPointer,
        rightPointer: action.payload.rightPointer,
      });

      object.width = 100;
      object.height = 100;
      const w = object.width;
      const h = object.height;
      object.path[0][1] = 0 - w / 2;
      object.path[0][2] = 0 - h / 2;

      object.path[1][1] = object.path[0][1] + w;
      object.path[1][2] = object.path[0][2] + h;

      handleDynamicLine(object);
    },
    setLeftPointer: (state, action) => {
      const object = instance.getActiveObject();
      const { pointer } = { ...action.payload };

      object.set({
        leftPointer: pointer,
      });
      instance.requestRenderAll();
    },
    setRightPointer: (state, action) => {
      const object = instance.getActiveObject();
      const { pointer } = { ...action.payload };

      object.set({
        rightPointer: pointer,
      });
      instance.requestRenderAll();
    },
    addCircleObject: (state, action) => {
      if (!state.isCanvasEditable) {
        return;
      }

      const circle = new fabric.Ellipse({
        fill: '#000',
      });
      circle.set({
        rx: 150,
        ry: 150,
      });
      handleDynamicShape(circle);
    },
    addGaussianObject: (state, action) => {
      if (!state.isCanvasEditable) {
        return;
      }

      const object = new fabric.Path('M0 0C0 0 0 0 0 0Z', {
        stroke: '#000000aa',
        strokeWidth: 5,
        fill: 'transparent',
      });

      const width = 300;
      const height = 300;

      // m 0 200 C 200 200 150 100 200 100 C 250 100 200 200 400 200 V 200
      const pathString = `m ${-width / 2} ${height / 2} C 0 ${height / 2} ${-(width / 10)} ${
        -height / 2
      } 0 ${-height / 2} C ${width / 10} ${-height / 2} 0 ${height / 2} ${width / 2} ${height / 2}`;
      const newPath = new fabric.Path(pathString);

      object.set({
        path: newPath.path,
        width: width,
        height: height,
      });
      handleDynamicShape(object);
    },
    addOgiveObject: (state, action) => {
      if (!state.isCanvasEditable) {
        return;
      }

      const ogive = new fabric.Path('');

      const width = 300;
      const height = 300;

      const pathString = `M0 -${height / 2} C-${width / 2} -${height / 5} -${width / 2} -${
        height / 5
      } -${width / 2} 0 v${height / 2} h${width} V0 C${width / 2} -${height / 5} ${width / 2} -${
        height / 5
      } 0 -${height / 2}Z`;
      const newPath = new fabric.Path(pathString);

      ogive.set({
        path: newPath.path,
        width: width,
        height: height,
      });
      handleDynamicShape(ogive);
    },
    addSemicircleObject: (state, action) => {
      if (!state.isCanvasEditable) {
        return;
      }

      const object = new fabric.Path('M0 0C0 0 0 0 0 0Z', {
        stroke: '#000',
        strokeWidth: 4,
        fill: '#000',
        leftPointer: action.payload.leftPointer,
        rightPointer: action.payload.rightPointer,
      });

      const width = 300;
      const height = 150;
      const halfWidth = width / 2;
      const halfHeight = height / 2;

      object.set({
        width,
        height,
      });

      object.path = [
        ['M', -halfWidth, halfHeight],
        ['C', -halfWidth, -halfHeight / 0.6, halfWidth, -halfHeight / 0.6, halfWidth, halfHeight],
      ];
      handleDynamicShape(object);
    },
    addTextObject: (state, action) => {
      const { content, fontFamily, fontSize } = { ...action.payload };
      const text = new fabric.Textbox(content, {
        fontFamily: fontFamily,
        fontSize: fontSize,
        left: CANVAS_DIMENSIONS.width / 2 - 125,
        top: CANVAS_DIMENSIONS.height / 2,
        width: 250,
      });
      withoutAutoRender(() => instance.add(text));
      setTimeout(() => {
        instance.setActiveObject(text);
        instance.requestRenderAll();
      }, 100);
    },
    addImageObject: (state, action) => {
      const canvasDimensions = current(state).currentDimensions;
      fabric.MTGImage.fromURL(
        action.payload.imgUrl,
        function (object) {
          object.set({ src: action.payload.imgUrl });

          const objectOriginalWidth = object.getScaledWidth();
          const objectOriginalHeight = object.getScaledHeight();

          const notTallEnough = objectOriginalHeight < canvasDimensions.height * 0.2;
          const notWideEnough = objectOriginalWidth < canvasDimensions.width * 0.2;

          if (notTallEnough) {
            object.scaleToHeight(canvasDimensions.height / 2);
          } else if (notWideEnough) {
            object.scaleToWidth(canvasDimensions.width / 2);
          }

          const objectWidth = object.getScaledWidth();
          const objectHeight = object.getScaledHeight();

          const tooTall = objectHeight > canvasDimensions.height * 0.8;
          const tooWide = objectWidth > canvasDimensions.width * 0.8;

          if (tooTall) {
            object.scaleToHeight(canvasDimensions.height / 2);
          }
          if (tooWide) {
            object.scaleToWidth(canvasDimensions.width / 2);
          }

          const { centerX, centerY } = placeObjectOnCanvas(object);
          object.left = centerX;
          object.top = centerY;

          addWatermark(object, function (watermark) {
            withoutAutoRender(() => {
              instance.add(object);
              instance.setActiveObject(object);
              watermark?.bringForward();
            });
            instance.requestRenderAll();
          });
        },
        {
          crossOrigin: 'anonymous',
          imgType: action.payload.imgType,
          imgUrl: action.payload.imgUrl,
          imgId: action.payload.imgId,
          hasColor: action.payload.hasColor,
          tags: action.payload.tags,
          chartType: action.payload.chartType,
          chartData: action.payload.chartData,
        },
      );
    },
    addEditedObject: (state, action) => {
      fabric.MTGImage.fromURL(
        action.payload.imgUrl,
        function (object) {
          object.set({
            left: action.payload.left,
            top: action.payload.top,
            src: action.payload.imgUrl,
            width: action.payload.width,
            height: action.payload.height,
            scaleX: action.payload.scaleX,
            scaleY: action.payload.scaleY,
            angle: action.payload.angle,
            imgType: action.payload.imgType,
          });

          instance.add(object);
          instance.setActiveObject(object);
          instance.requestRenderAll();
        },
        {
          crossOrigin: 'anonymous',
          imgType: action.payload.imgType,
          imgUrl: action.payload.imgUrl,
          imgId: action.payload.imgId,
          hasColor: action.payload.hasColor,
          tags: action.payload.tags,
          chartType: action.payload.chartType,
          chartData: action.payload.chartData,
        },
      );
    },
    groupSvgParts: (state, action) => {
      if (!state.isCanvasEditable) {
        return;
      }

      const activeObject = instance.getActiveObject();
      if (!activeObject) {
        return;
      }

      const objects =
        activeObject.type === 'activeSelection' ? activeObject.getObjects() : [activeObject];

      // Check if all objects are SVG parts from the same original SVG
      const svgParts = objects.filter((obj) => obj.imgType === 'svg_part' && obj.originalSvgId);

      // Verify all SVG parts have the same originalSvgId
      if (svgParts.length > 1) {
        const firstOriginalSvgId = svgParts[0].originalSvgId;
        const allSameOrigin = svgParts.every((part) => part.originalSvgId === firstOriginalSvgId);

        if (!allSameOrigin) {
          console.log('Cannot group SVG parts: not all parts have the same originalSvgId');
          return;
        }

        // Create a group from SVG parts
        const group = new fabric.Group(svgParts, {
          imgType: 'svg_parts_group',
          originalSvgId: firstOriginalSvgId,
          selectable: true,
          evented: true,
        });

        // Remove individual parts and add group
        svgParts.forEach((part) => instance.remove(part));
        instance.add(group);
        instance.setActiveObject(group);
        instance.requestRenderAll();
      }
    },
    highlightRelatedSvgParts: (state, action) => {
      // This reducer just sets a flag to trigger the highlighting
      // The actual canvas manipulation happens in the async thunk
      return;
    },
    setImageSource: (state, action) => {
      const object = instance.getActiveObject();
      const { url, hasColor } = { ...action.payload };

      fabric.MTGImage.fromURL(url, function (img) {
        img.set({
          angle: object.angle,
          flipX: object.flipX,
          flipY: object.flipY,
          filters: object.filters,
          hasColor: hasColor,
          height: object.height,
          imgId: object.imgId,
          imgType: object.imgType,
          imgUrl: url,
          left: object.left,
          opacity: object.opacity,
          scaleX: object.scaleX,
          scaleY: object.scaleY,
          src: object.imgUrl,
          top: object.top,
          width: object.width,
          clipPath: object.clipPath, // Persist the clipPath
        });

        instance.remove(
          instance.getObjects().filter((o) => o.watermarkTo && o.watermarkTo === object.uuid)[0],
        );
        instance.add(img);

        instance.remove(object);

        addWatermark(img, function (watermark) {
          instance.setActiveObject(img);
          instance.fire('selection:created');
        });
      });
    },
    setImageColor: (state, action) => {
      const object = instance.getActiveObject();
      const { color } = { ...action.payload };

      if (object.imgType === CanvasToolbarEnum.DYNAMIC_SHAPE) {
        object.set({
          fill: color,
        });
        instance.requestRenderAll();
        return;
      }

      // Handle SVG parts (individual objects created from ungrouped SVG)
      if (object.imgType === 'svg_part') {
        object.set({
          fill: color,
        });
        instance.requestRenderAll();
        return;
      }

      const updateSvgColor = (svgElement) => {
        if (object.imgType === 'icon') {
          svgElement.style.fill = color;
        }

        const changeColorElements = svgElement.querySelectorAll(
          'g[id*="changecolor"] [fill], [id*="changecolor"]',
        );
        if (changeColorElements) {
          Array.from(changeColorElements).map((element) => element.setAttribute('fill', color));
        }

        const changeStrokeColorElements = svgElement.querySelector('[id*="changestrokecolor"]');
        if (changeStrokeColorElements) {
          Array.from(changeStrokeColorElements).map((element) =>
            element.setAttribute('stroke', color),
          );
        }

        const imgData =
          'data:image/svg+xml;base64,' + btoa(new XMLSerializer().serializeToString(svgElement));

        object.setSrc(imgData, () => {
          instance.requestRenderAll();
        });
      };

      const imgElement = object.getElement();
      const isBase64 = imgElement.src.startsWith('data:image');

      if (isBase64) {
        const svg = atob(imgElement.src.split(',')[1]);
        const svgElement = new DOMParser().parseFromString(svg, 'image/svg+xml').documentElement;
        updateSvgColor(svgElement);
        return;
      }

      fetch(object.imgUrl)
        .then((data) => data.text())
        .then((svg) => {
          const svgDOM = new DOMParser().parseFromString(svg, 'image/svg+xml');
          const svgElement = svgDOM.getElementsByTagName('svg')[0];
          updateSvgColor(svgElement);
        });
    },
    setShapeStrokeColor: (state, action) => {
      const object = instance.getActiveObject();
      const { color } = { ...action.payload };

      object.set({
        stroke: color,
      });
      instance.requestRenderAll();
    },
    setShapeStrokeWidth: (state, action) => {
      const object = instance.getActiveObject();
      const { width } = { ...action.payload };

      object.set({
        strokeWidth: width / 10,
      });
      instance.requestRenderAll();
    },
    setShapeStrokeStyle: (state, action) => {
      const object = instance.getActiveObject();
      const { style } = { ...action.payload };

      object.set({
        strokeDashArray: style,
      });
      instance.requestRenderAll();
    },
    setTextColor: (state, action) => {
      const activeObject = instance.getActiveObject();
      const { color } = { ...action.payload };

      const applyTextColor = (object) => {
        if (object.isEditing) {
          object.setSelectionStyles({
            fill: color,
          });
        } else {
          object.removeStyle('fill');
          object.set({
            fill: color,
          });
        }
      };

      if (
        [CanvasToolbarEnum.ACTIVE_SELECTION, CanvasToolbarEnum.GROUP].includes(activeObject.type)
      ) {
        const handleNestedObjects = (parentObject) => {
          parentObject.getObjects().map((object) => {
            if (object.type === CanvasToolbarEnum.GROUP) {
              return handleNestedObjects(object);
            }

            applyTextColor(object);
          });
          return true;
        };
        handleNestedObjects(activeObject);
      } else {
        applyTextColor(activeObject);
      }

      instance.requestRenderAll();
    },
    setTextBackgroundColor: (state, action) => {
      const activeObject = instance.getActiveObject();
      const { color } = { ...action.payload };

      const applyBackgroundColor = (object) => {
        if (object.isEditing) {
          object.setSelectionStyles({
            textBackgroundColor: color,
          });
        } else {
          object.removeStyle('textBackgroundColor');
          object.set({
            backgroundColor: color,
          });
        }
      };

      if (
        [CanvasToolbarEnum.ACTIVE_SELECTION, CanvasToolbarEnum.GROUP].includes(activeObject.type)
      ) {
        const handleNestedObjects = (parentObject) => {
          parentObject.getObjects().map((object) => {
            if (object.type === CanvasToolbarEnum.GROUP) {
              return handleNestedObjects(object);
            }

            applyBackgroundColor(object);
          });
          return true;
        };
        handleNestedObjects(activeObject);
      } else {
        applyBackgroundColor(activeObject);
      }

      instance.requestRenderAll();
    },
    setTextEffect: (state, action) => {
      const activeObject = instance.getActiveObject();
      const { effect } = { ...action.payload };

      const applyTextEffect = (object, effect) => {
        switch (effect) {
          case 'shadow':
            const shadow = new fabric.Shadow({
              color: 'rgba(0, 0, 0, 0.4)',
              blur: 1,
              offsetX: -2,
              offsetY: 2,
            });

            object.set({
              shadow: shadow,
            });

            break;
          case 'outline':
            if (['#000000', '#000', 'rgb(0,0,0)'].includes(object.get('fill'))) {
              object.set({ fill: '#ffffff' });
            }

            object.set({
              strokeWidth: 1,
              stroke: '#000000',
            });

            break;
          case 'none':
          default:
            if (['#ffffff', '#fff', 'rgb(255,255,255)'].includes(object.get('fill'))) {
              object.set({ fill: '#000000' });
            }

            object.set({
              shadow: null,
              strokeWidth: 0,
            });

            break;
        }
      };

      if (
        [CanvasToolbarEnum.ACTIVE_SELECTION, CanvasToolbarEnum.GROUP].includes(activeObject.type)
      ) {
        const handleNestedObjects = (parentObject) => {
          parentObject.getObjects().map((object) => {
            if (object.type === CanvasToolbarEnum.GROUP) {
              return handleNestedObjects(object);
            }

            applyTextEffect(object, effect);
          });
          return true;
        };
        handleNestedObjects(activeObject);
      } else {
        applyTextEffect(activeObject, effect);
      }

      instance.requestRenderAll();
    },
    handleGrid: (state, action) => {
      const { width, height } = state.currentDimensions;

      let open = action && action.payload ? action.payload.open : state.grid.open;

      const left = (CANVAS_DIMENSIONS.width - width) / 2;
      const top = (CANVAS_DIMENSIONS.height - height) / 2;

      state.grid.open = open;

      instance.remove(instance.getItemByName('grid'));

      if (open) {
        const group = new fabric.Group();
        for (let i = 0; i < height / state.grid.size; i++) {
          const lineHorizontal = new fabric.Line(
            [left, i * state.grid.size + top, left + width, i * state.grid.size + top],
            {
              stroke: '#ccc',
              strokeWidth: 1,
              selectable: false,
              hasControls: false,
              hasBorders: false,
            },
          );
          group.addWithUpdate(lineHorizontal);
        }
        for (let i = 0; i < width / state.grid.size; i++) {
          const lineVertical = new fabric.Line(
            [i * state.grid.size + left, top, i * state.grid.size + left, top + height],
            {
              stroke: '#ccc',
              strokeWidth: 1,
              selectable: false,
              hasControls: false,
              hasBorders: false,
            },
          );
          group.addWithUpdate(lineVertical);
        }

        group.name = 'grid';
        group.selectable = false;
        group.hasBorders = false;
        group.hasControls = false;
        group.objectCaching = false;
        group.excludeFromExport = true;
        instance.add(group);
        instance.requestRenderAll();
        instance.sendToBack(group);
        return;
      }
    },
    updateGridSize: (state, action) => {
      state.grid.size = action.payload;
    },
    updateZoom: (state, action) => {
      state.zoom = action.payload;
      const center = instance.getCenter();
      instance.zoomToPoint(
        {
          x: center.left,
          y: center.top,
        },
        state.zoom,
      );
    },
    enableAlignmentGuides: (state) => {
      state.alignmentGuides = true;

      handleAlignmentGuides(instance);
    },
    disableAlignmentGuides: (state) => {
      state.alignmentGuides = false;

      cancelAlignmentGuides(instance);
    },
    convertLine: (state, action) => {
      const object = instance.getActiveObject();
      const path = object.path;

      if (action.payload.toCurve) {
        if (path[1][0] === 'L') {
          path[1][0] = 'Q';
          path[1][3] = path[1][1];
          path[1][4] = path[1][2];
          path[1][1] = path[1][2] = 0;
        }
      } else {
        if (path[1][0] === 'Q') {
          path[1][0] = 'L';
          path[1][1] = path[1][3];
          path[1][2] = path[1][4];
          path[1].splice(3, 2);
        }
      }

      object.set({
        path: path,
      });
    },
    addTextBlock: (state, action) => {
      const { content } = { ...action.payload };

      fabric.util.enlivenObjects(content.objects, (elements) => {
        let group = new fabric.Group();

        elements.map((obj) => {
          obj.set({
            objectCaching: true,
            hasControls: true,
            imgType: null,
          });
          if (obj.type !== 'textbox') {
            obj.set({ imgType: 'dynamic_shape' });
          } else {
            if (!obj.styles) {
              obj.styles = [];
            }
          }
          group.addWithUpdate(obj);
        });

        group.set({
          left: (CANVAS_DIMENSIONS.width - group.width) / 2,
          top: (CANVAS_DIMENSIONS.height - group.height) / 2,
        });

        withoutAutoRender(() => instance.add(group));
        setTimeout(() => {
          instance.setActiveObject(group);
          instance.requestRenderAll();
          group.toActiveSelection();
        }, 100);
      });
    },
    updateToolbarPosition: (state, action) => {
      state.toolbarPosition = action.payload;
    },
    setSelectedTemplate: (state, action) => {
      state.selectedTemplate = action.payload;
    },
    setIsInputFocused: (state, action) => {
      state.isInputFocused = action.payload;
    },
    setKeepSidePanelOpen: (state, action) => {
      state.keepSidePanelOpen = action.payload;
    },
    setSelectedChart: (state, action) => {
      state.selectedChart = action.payload;
    },
    resetCanvasHistoric: (state, action) => {
      state.redoStack.length = state.undoStack.length = 0;
      state.currentState = JSON.stringify(instance.toJSON());
    },

    enableDrawingMode: (state) => {
      try {
        if (!state.isCanvasEditable) {
          return;
        }

        instance.isDrawingMode = true;
        instance.freeDrawingBrush = new fabric.PencilBrush(instance);
        instance.freeDrawingBrush.width = 1.5;
        instance.freeDrawingBrush.color = '#000000';
      } catch (error) {
        console.error('Error enabling drawing mode:', error);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(historicSaveAction.fulfilled, (state, action) => {
        if (!action.payload) {
          return;
        }
        const { currentState, undoEntry } = action.payload;
        // Update current state (covers baseline refreshes and normal saves)
        state.currentState = currentState;
        // Only push to undo stack if there's an actual undo entry
        if (undoEntry) {
          // Maintain max undo stack size
          if (state.undoStack.length >= MAX_UNDO_STACKS) {
            state.undoStack.shift();
          }
          // Add to undo stack
          state.undoStack.push(undoEntry);
          // Clear redo stack when new action is performed
          state.redoStack = [];
        }
      })
      .addCase(undoAction.pending, (state) => {
        state.undoBusy = true;
      })
      .addCase(undoAction.fulfilled, (state, action) => {
        if (action.payload) {
          state.undoStack = action.payload.undoStack;
          state.redoStack = action.payload.redoStack;
          state.currentState = action.payload.currentState;
        }
        state.undoBusy = false;
      })
      .addCase(undoAction.rejected, (state, action) => {
        console.error('Undo action failed:', action.error);
        state.undoBusy = false;
      })
      .addCase(redoAction.pending, (state) => {
        state.undoBusy = true;
      })
      .addCase(redoAction.fulfilled, (state, action) => {
        if (action.payload) {
          state.undoStack = action.payload.undoStack;
          state.redoStack = action.payload.redoStack;
          state.currentState = action.payload.currentState;
        }
        state.undoBusy = false;
      })
      .addCase(redoAction.rejected, (state, action) => {
        console.error('Redo action failed:', action.error);
        state.undoBusy = false;
      });
  },
});

export const {
  reset,
  setSaving,
  setNotSaving,
  updateLastSaveDate,
  openToolbar,
  closeToolbar,
  openUpgradeOfferBanner,
  closeUpgradeOfferBanner,
  openCountdownTrialBanner,
  closeCountdownTrialBanner,
  openDownloadProgressBar,
  closeDownloadProgressBar,
  openTopBarTrialOfferBanner,
  closeTopBarTrialOfferBanner,
  setUser,
  setIsCanvasEditable,
  init,
  end,
  addLineObject,
  addWavyLineObject,
  addTextObject,
  addCircleObject,
  addOgiveObject,
  addGaussianObject,
  addTextBlock,
  addImageObject,
  addEditedObject,
  groupSvgParts,
  highlightRelatedSvgParts,
  addTriangleObject,
  addPolygonObject,
  addTrapezoidObject,
  addPillObject,
  addRectangleObject,
  addSemicircleObject,
  addStraightLineObject,
  setLeftPointer,
  setRightPointer,
  setShapeStrokeStyle,
  setShapeStrokeColor,
  setShapeStrokeWidth,
  setImageColor,
  setImageSource,
  setTextColor,
  setTextBackgroundColor,
  setTextEffect,
  setCurrentPointer,
  setCanvasBackground,
  scrollToCenter,
  updateContent,
  setCurrentDimensions,
  addNewComment,
  handleGrid,
  updateGridSize,
  updateZoom,
  enableAlignmentGuides,
  disableAlignmentGuides,
  convertLine,
  undo,
  redo,
  updateToolbarPosition,
  setSelectedTemplate,
  setIsInputFocused,
  setKeepSidePanelOpen,
  setSelectedChart,
  resetCanvasHistoric,
  setIsCanvasLoading,
  enableDrawingMode,
} = canvasSlice.actions;

// AD:TODO: remove this function after 2025-05-30
export const loadSlideData_old = createAsyncThunk(
  'creation/loadSlideData',
  async (data, thunkAPI) => {
    thunkAPI.dispatch(setIsCanvasLoading(true));
    const content = data.content
      ? data.content
      : await CreationService.getSlideContent(data.creationId, data.slideId);
    const isCanvasEditable = thunkAPI.getState().canvas.isCanvasEditable;

    if (data.user) {
      const creation = await CreationService.findOne(data.creationId);
      // const allCreations = await CreationService.findAll({ status: 'available' });
      isTrial = creation.data.tp_user === UserTypeEnum.TRIAL;
      // allUserCreations = allCreations.data;
      userInfo = data.user;
    }

    // set the slide id in the content(canvas instance) object
    content.slideId = data.slideId || null;
    await updateCanvasContent(content, isCanvasEditable);

    thunkAPI.dispatch(resetCanvasHistoric());
    thunkAPI.dispatch(scrollToCenter());
    thunkAPI.dispatch(setIsCanvasLoading(false));
  },
);

export const loadSlideData = createAsyncThunk('creation/loadSlideData', async (data, thunkAPI) => {
  const metrics = {
    total: 0,
    dataFetching: 0,
    canvasLoading: 0,
    objectProcessing: 0,
    watermarkProcessing: 0,
    finalRendering: 0,
  };

  const startTotal = window.performance.now();

  try {
    thunkAPI.dispatch(setIsCanvasLoading(true));

    const startDataFetching = window.performance.now();
    const [content, isCanvasEditable] = await Promise.all([
      data.content
        ? Promise.resolve(data.content)
        : CreationService.getSlideContent(data.creationId, data.slideId),
      Promise.resolve(thunkAPI.getState().canvas.isCanvasEditable),
    ]);
    metrics.dataFetching = window.performance.now() - startDataFetching;

    if (data.user) {
      const creation = await CreationService.findOne(data.creationId);
      isTrial = creation.data.tp_user === UserTypeEnum.TRIAL;
      userInfo = data.user;
    }

    content.slideId = data.slideId || null;
    instance.renderOnAddRemove = false;

    const startCanvasLoading = window.performance.now();
    await new Promise((resolve) => {
      instance.loadFromJSON(updateCFBucketUrls(content), async () => {
        metrics.canvasLoading = window.performance.now() - startCanvasLoading;

        const startObjectProcessing = window.performance.now();
        const watermarkPromises = [];
        const canvasObjects = instance.getObjects();

        canvasObjects.forEach((obj) => {
          // Remove existing watermarks first
          if (obj.src && obj.src.includes('images/watermark')) {
            instance.remove(obj);
          } else {
            if (obj.type === 'DynamicLine') {
              obj.set({ left: obj.left, top: obj.top, pathOffset: { x: 0, y: 0 } });
            }
            if (obj.imgType?.startsWith('comment') && !getShowCommentsFromLocalStorage()) {
              obj.set({ active: false, visible: false, selectable: false, opacity: 0 });
            }
            if (!isCanvasEditable) {
              obj.set({ selectable: false, evented: false });
            }
            if (
              isTrial &&
              obj.type === 'MTGImage' &&
              [CanvasToolbarEnum.ILLUSTRATION, CanvasToolbarEnum.ICON].includes(obj.imgType)
            ) {
              watermarkPromises.push(new Promise((resolve) => addWatermark(obj, resolve)));
            }
          }
        });

        metrics.objectProcessing = window.performance.now() - startObjectProcessing;

        const startWatermarkProcessing = window.performance.now();
        await Promise.all(watermarkPromises);
        metrics.watermarkProcessing = window.performance.now() - startWatermarkProcessing;

        const startFinalRendering = window.performance.now();
        instance.renderOnAddRemove = true;
        instance.requestRenderAll();
        metrics.finalRendering = window.performance.now() - startFinalRendering;

        resolve();
      });
    });

    await Promise.all([
      thunkAPI.dispatch(resetCanvasHistoric()),
      thunkAPI.dispatch(scrollToCenter()),
    ]);

    metrics.total = window.performance.now() - startTotal;

    // Send to Datadog RUM
    const objectCount = instance.getObjects().length;
    const basePayload = {
      creationId: data.creationId,
      slideId: data.slideId,
      tpUser: userInfo?.tp_user,
      objectCount,
      userId: userInfo?.id,
    };

    // Object.entries(metrics).forEach(([name, value]) =>
    //   trackCanvasMetric(name, { ...basePayload, value }),
    // );

    // trackCanvasMetric('canvas.load_complete', { // Removed: @datadog/browser-rum package removed
    //   ...basePayload,
    //   totalTime: metrics.total,
    // });
  } catch (error) {
    logCanvasError('Canvas failed to load in loadSlideData', {
      error,
      creationId: data.creationId,
      slideId: data.slideId,
      tpUser: userInfo?.tp_user,
      objectCount: instance?.getObjects()?.length,
      userId: userInfo?.id,
      location: 'loadSlideData',
    });
  } finally {
    thunkAPI.dispatch(setIsCanvasLoading(false));
  }
});

export const breakApartSvgAction = createAsyncThunk(
  'canvas/breakApartSvg',
  async (_, { getState }) => {
    const state = getState().canvas;

    if (!state.isCanvasEditable) {
      return;
    }

    const svgObject = instance.getActiveObject();
    if (
      !svgObject ||
      !('type' in svgObject) ||
      svgObject.type == null ||
      !['MTGImage'].some(
        (type) => svgObject.type && svgObject.type.toLowerCase() === type.toLowerCase(),
      )
    ) {
      return;
    }

    // Get the SVG source
    const svgSrc = svgObject.getSrc();

    const processSvg = (svgString) => {
      return new Promise((resolve) => {
        fabric.loadSVGFromString(svgString, (objects, options) => {
          if (objects && objects.length > 1) {
            const outerGroup = svgObject;
            const origin = outerGroup.getPointByOrigin('left', 'top');
            const preserved = [];

            // Create a unique identifier combining multiple elements
            const timestamp = Date.now();
            const randomId = Math.random().toString(36).substring(2, 9);
            const originalImgId = outerGroup.imgId || 'unknown';
            const objectPosition = `${Math.round(outerGroup.left)}_${Math.round(outerGroup.top)}`;
            const objectScale = `${(outerGroup.scaleX || 1).toFixed(2)}_${(outerGroup.scaleY || 1).toFixed(2)}`;

            const uniqueOriginalSvgId = `${originalImgId}_${objectPosition}_${objectScale}_${timestamp}_${randomId}`;

            // Remove the original SVG object
            instance.remove(outerGroup);

            // Use clone logic to create individual objects from SVG elements
            objects.forEach((obj, index) => {
              obj.clone((clone) => {
                // Apply the original SVG object's transformations
                const originalScaleX = outerGroup.scaleX || 1;
                const originalScaleY = outerGroup.scaleY || 1;
                const originalAngle = outerGroup.angle || 0;

                // Calculate the proper position with transformations
                clone.left = origin.x + obj.left * originalScaleX;
                clone.top = origin.y + obj.top * originalScaleY;
                clone.angle = originalAngle + (obj.angle || 0);
                clone.scaleX = (obj.scaleX || 1) * originalScaleX;
                clone.scaleY = (obj.scaleY || 1) * originalScaleY;

                clone.set({
                  selectable: true,
                  evented: true,
                  imgType: 'svg_part',
                  originalSvgId: uniqueOriginalSvgId,
                  partIndex: index,
                });

                preserved.push(clone);
                instance.add(clone);
              });
            });

            // Select all parts
            instance.setActiveObject(new fabric.ActiveSelection(preserved, { canvas: instance }));
            instance.requestRenderAll();
          }
          resolve();
        });
      });
    };

    if (svgSrc.startsWith('data:image/svg+xml')) {
      // Extract SVG from data URL
      const base64Data = svgSrc.split(',')[1];
      const svgString = atob(base64Data);
      await processSvg(svgString);
    } else {
      // Fetch SVG from URL
      const response = await fetch(svgSrc);
      const svgString = await response.text();
      await processSvg(svgString);
    }
  },
);

const reloadCanvasWithJSON = (currentState) => {
  return new Promise((resolve) => {
    instance.renderOnAddRemove = false;

    instance
      .loadFromJSON(updateCFBucketUrls(JSON.stringify(currentState)), () => {
        const watermarkPromises = [];

        instance.forEachObject((obj) => {
          if (obj.controlPoint) {
            instance.remove(obj);
          }
          if (obj.src && obj.src.includes('images/watermark')) {
            instance.remove(obj);
          } else if (
            isTrial &&
            obj.type === 'MTGImage' &&
            [CanvasToolbarEnum.ILLUSTRATION, CanvasToolbarEnum.ICON].includes(obj.imgType)
          ) {
            watermarkPromises.push(new Promise((resolve) => addWatermark(obj, resolve)));
          }
        });

        if (watermarkPromises.length) {
          Promise.all(watermarkPromises).then(() => {
            instance.renderOnAddRemove = true;
            instance.requestRenderAll();
            resolve();
          });
        } else {
          instance.renderOnAddRemove = true;
          instance.requestRenderAll();
          resolve();
        }
      })
      .requestRenderAll();
  });
};

export const undoAction = createAsyncThunk('canvas/undo', async (_, { getState, dispatch }) => {
  const state = getState().canvas;

  // Check if there's anything to undo
  if (!state.undoStack.length) {
    return null;
  }

  const undoStack = [...state.undoStack];
  const redoStack = [...state.redoStack];
  const delta = undoStack.pop();

  // Get current canvas state
  const instanceJSON = instance.toJSON();

  let newState;
  try {
    // Apply inverse delta to get previous state
    newState = jdp.patch(instanceJSON, delta.inverse);
  } catch (err) {
    console.error('Failed to apply undo delta:', err);
    throw new Error(`Undo failed: ${err.message}`);
  }

  try {
    // Reload canvas with new state
    await reloadCanvasWithJSON(newState);

    // Update grid if needed
    dispatch(handleGrid());

    // Store the forward delta for redo
    redoStack.push(delta);

    return {
      undoStack,
      redoStack,
      currentState: JSON.stringify(newState),
    };
  } catch (err) {
    console.error('Failed to reload canvas during undo:', err);
    // Restore the undo stack if reload fails
    undoStack.push(delta);
    throw new Error(`Failed to reload canvas: ${err.message}`);
  }
});

export const redoAction = createAsyncThunk('canvas/redo', async (_, { getState, dispatch }) => {
  const state = getState().canvas;

  // Check if there's anything to redo
  if (!state.redoStack.length) {
    return null;
  }

  const undoStack = [...state.undoStack];
  const redoStack = [...state.redoStack];
  const delta = redoStack.pop();

  // Get current canvas state
  const instanceJSON = instance.toJSON();

  let newState;
  try {
    // Apply forward delta to get next state
    newState = jdp.patch(instanceJSON, delta.delta);
  } catch (err) {
    console.error('Failed to apply redo delta:', err);
    throw new Error(`Redo failed: ${err.message}`);
  }

  try {
    // Reload canvas with new state
    await reloadCanvasWithJSON(newState);

    // Update grid if needed
    dispatch(handleGrid());

    // Store the delta in undo stack
    undoStack.push(delta);

    return {
      undoStack,
      redoStack,
      currentState: JSON.stringify(newState),
    };
  } catch (err) {
    console.error('Failed to reload canvas during redo:', err);
    // Restore the redo stack if reload fails
    redoStack.push(delta);
    throw new Error(`Failed to reload canvas: ${err.message}`);
  }
});

export const applyTemplateToCreation = createAsyncThunk(
  'creation/applyTemplateToCreation',
  async (data, thunkAPI) => {
    const generalState = thunkAPI.getState().general;
    const activeSlide = generalState.activeSlide;

    thunkAPI.dispatch(
      setCurrentDimensions({
        width: data.width,
        height: data.height,
      }),
    );

    thunkAPI.dispatch(
      saveSlidesInfo({
        creationId: generalState.creation.id,
        slideInfo: {
          width: data.width,
          height: data.height,
        },
      }),
    );

    const dataReq = {
      creationId: generalState.creation.id,
      activeSlide,
    };

    try {
      const response = await TemplatesService.applyTemplateToCreation(data.templateId, dataReq);
    } catch (err) {
      console.error(err);
    }
  },
);

export const saveActiveSlideData = createAsyncThunk(
  'creation/saveActiveSlideData',
  async (data, thunkAPI) => {
    const canvasState = thunkAPI.getState().canvas;

    const withSnack = data?.withSnack || false;
    thunkAPI.dispatch(setSaving());

    if (withSnack) {
      thunkAPI.dispatch(showSnackbar({ text: 'Saving...', severity: 'processing' }));
    }

    const generalState = thunkAPI.getState().general;

    // DO not save the content if the content(read canvas) slide is not same as the
    // one received from the state
    if (instance?.slideId !== generalState.activeSlide.id) {
      thunkAPI.dispatch(setNotSaving());
      throw new Error(
        `slide-save-error: Slide id mismatch. \n state.slide = ${generalState.activeSlide.id}, instance.slide = ${instance?.slideId}`,
      );
    }

    if (generalState.creation) {
      try {
        // Skip costly canvas clones; updateSlideHelper serializes the live instance
        // via serializeCanvasForSave so locks/SVG metadata survive and control points stay out
        await updateSlideHelper(instance, generalState, canvasState, thunkAPI.dispatch);
      } catch (e) {
        console.error('Error while saving slide:', e);
        throw e;
      }

      if (withSnack) {
        thunkAPI.dispatch(showSnackbar({ text: 'Slide saved successfully', severity: 'success' }));
      }
    }

    thunkAPI.dispatch(updateLastSaveDate());
    thunkAPI.dispatch(setNotSaving());
  },
);

export const highlightRelatedSvgPartsAsync = createAsyncThunk(
  'canvas/highlightRelatedSvgParts',
  async (_, { getState }) => {
    const state = getState();

    if (!state.canvas.isCanvasEditable) {
      return;
    }

    const activeObject = instance.getActiveObject();
    if (!activeObject || activeObject.imgType !== 'svg_part') {
      return;
    }

    const originalSvgId = activeObject.originalSvgId;
    if (!originalSvgId) {
      console.warn('Cannot highlight related SVG parts: active object missing originalSvgId');
      return;
    }

    // Find all SVG parts with the same original SVG ID
    const relatedSvgParts = instance.getObjects().filter((obj) => {
      return (
        obj &&
        obj.imgType === 'svg_part' &&
        obj.originalSvgId &&
        obj.originalSvgId === originalSvgId
      );
    });

    if (relatedSvgParts.length > 1) {
      // Clear any existing selection
      instance.discardActiveObject();

      // Create an active selection with all related SVG parts
      const selection = new fabric.ActiveSelection(relatedSvgParts, {
        canvas: instance,
      });

      instance.setActiveObject(selection);
      instance.requestRenderAll();
    }
  },
);

export const updateSlideHelper = async (instance, generalState, canvasState, dispatch) => {
  const attemptUpdate = async (retryCount = 0) => {
    try {
      // Capture a lightweight, metadata-rich snapshot for persistence instead of cloning
      const customSerialization = serializeCanvasForSave(instance);
      const content = JSON.stringify(customSerialization);

      let previewBase64;
      const originalZoom = instance.getZoom();
      const originalWidth = instance.getWidth();
      const originalHeight = instance.getHeight();

      try {
        instance.setZoom(1);
        instance.setWidth(CANVAS_DIMENSIONS.width);
        instance.setHeight(CANVAS_DIMENSIONS.height);

        previewBase64 = getImageBase64Url({
          instance: instance,
          dimensions: canvasState.currentDimensions,
          zoom: 1,
          multiplier: 0.5,
        });
      } finally {
        instance.setZoom(originalZoom);
        instance.setWidth(originalWidth);
        instance.setHeight(originalHeight);
      }

      // Make the API call with the properly serialized content
      await CreationService.updateSlideContent(
        generalState.creation.id,
        generalState.activeSlide.id,
        {
          content: content,
          preview: extractBase64FromUrl(previewBase64),
        },
      );

      if (retryCount > 0) {
        dispatch(
          showSnackbar({
            text: 'Slide saved successfully',
            severity: 'success',
          }),
        );
      }
    } catch (error) {
      console.error('Error in updateSlideHelper:', error);

      if (retryCount < 4) {
        dispatch(
          showSnackbar({
            text: `Failed to save slide, retrying...`,
            severity: 'processing',
          }),
        );

        // Exponential backoff for retries: 1s, 2s, 4s, 8s
        await new Promise((resolve) => setTimeout(resolve, Math.pow(2, retryCount) * 1000));

        return attemptUpdate(retryCount + 1);
      } else {
        console.error('Final retry to save slide failed.', error);
        dispatch(setLoadError());
        dispatch(
          showSnackbar({
            text: 'Failed to save slide, Please refresh the page to continue.',
            severity: 'load_error',
          }),
        );
        throw error;
      }
    }
  };

  return attemptUpdate();
};

export const saveSlidesInfo = createAsyncThunk('creation/saveSlideInfo', async (data) => {
  await CreationService.updateSlideByCreation(data.creationId, data.slideInfo);
});

export const saveAllSlideImages = createAsyncThunk(
  'creation/saveAllSlideImages',
  async (data, thunkAPI) => {
    const canvasState = thunkAPI.getState().canvas;
    const generalState = thunkAPI.getState().general;

    generalState.creation.slides.map(async (slide) => {
      const slideData = await CreationService.getSlideContent(generalState.creation.id, slide.id);
      const tempCanvas = new fabric.Canvas();

      tempCanvas.loadFromJSON(updateCFBucketUrls(slideData.data.content), () => {
        const { width, height } = canvasState.currentDimensions;
        const base64Url = tempCanvas.toDataURL({
          format: 'png',
          width: width * canvasState.zoom,
          height: height * canvasState.zoom,
          left: (instance.getWidth() - width * canvasState.zoom) / 2,
          top: (instance.getHeight() - height * canvasState.zoom) / 2,
        });

        CreationService.downloadSlide(generalState.creation.id, slide.id, {
          content: extractBase64FromUrl(base64Url),
          format: 'png',
          dpi: 72,
          thumbnail: true,
        });
      });
    });
  },
);

export const downloadSlideImage = createAsyncThunk(
  'creation/downloadAllSlideImages',
  async ({ slides, ...data }, thunkAPI) => {
    const generalState = thunkAPI.getState().general;
    const canvasState = thunkAPI.getState().canvas;
    const { width, height } = canvasState.currentDimensions;
    const currentZoom = canvasState.zoom;

    for (let slide of slides) {
      const slideData = await CreationService.getSlideContent(generalState.creation.id, slide.id);
      let base64Url;

      if (data.format === 'svg') {
        const canvasHandler = new CanvasHandler(slideData.data.content, {
          ...data,
          preserveTexts: !data.convertSvgTexts,
        });

        try {
          const { result, externalResources } = await canvasHandler.generateSVG();
          const baseName = `${generalState.creation.name} - slide ${slide.sequence}`;

          await CreationService.downloadSlideAsSvg(generalState.creation.id, slide.id, {
            format: 'svg',
            thumbnail: false,
            email: data.email,
          });

          if (externalResources?.length) {
            await downloadFolder(result, externalResources, baseName);
          } else {
            const blob = new Blob([result], { type: 'image/svg+xml' });
            downloadBlob(blob, `${baseName}.svg`);
          }
        } catch (error) {
          console.error('Error downloading SVG:', error);
          openDownloadProgressBar({
            downloadingFile: false,
            downloadDone: false,
            downloadFailed: true,
          });
        }
      } else {
        await new Promise((resolve) => {
          const tempCanvas = new fabric.Canvas();

          tempCanvas.loadFromJSON(updateCFBucketUrls(slideData.data.content), async () => {
            processDynamicLines(tempCanvas).renderAll();

            if (data.transparentBackground) {
              tempCanvas.setBackgroundImage(null);
              tempCanvas.renderAll();
            }

            try {
              base64Url = tempCanvas.toDataURL({
                format: 'png',
                width: width * currentZoom,
                height: height * currentZoom,
                left: (instance.getWidth() - width * currentZoom) / 2,
                top: (instance.getHeight() - height * currentZoom) / 2,
                multiplier: data.dpi / 300,
              });
            } catch (error) {
              openDownloadProgressBar({
                downloadingFile: false,
                downloadDone: false,
                downloadFailed: true,
              }),
                console.error('Error in callback:', error);
            }

            const { data: blob } = await CreationService.downloadSlide(
              generalState.creation.id,
              slide.id,
              {
                content: extractBase64FromUrl(base64Url),
                format: data.format,
                dpi: data.dpi,
                thumbnail: false,
                grayscale: data.grayscale,
                email: data.email,
                cmyk: data.cmyk,
              },
            );

            downloadBlob(
              blob,
              `${generalState.creation.name} - slide ${slide.sequence}.${data.format}`,
            );

            resolve();
          });
        });
      }
    }

    thunkAPI.dispatch(
      openDownloadProgressBar({
        downloadingFile: false,
        downloadDone: true,
        downloadFailed: false,
      }),
    );
  },
);

export const downloadCreationPdf = createAsyncThunk(
  'creation/downloadCreationPdf',
  async (data, thunkAPI) => {
    try {
      const generalState = thunkAPI.getState().general;
      const canvasState = thunkAPI.getState().canvas;
      const { width, height } = canvasState.currentDimensions;
      const currentZoom = canvasState.zoom;

      for (let slideId of data.slidesId) {
        const slideData = await CreationService.getSlideContent(generalState.creation.id, slideId);
        let base64Url;

        await new Promise((resolve) => {
          const tempCanvas = new fabric.Canvas();

          tempCanvas.loadFromJSON(updateCFBucketUrls(slideData.data.content), async () => {
            processDynamicLines(tempCanvas).renderAll();

            base64Url = tempCanvas.toDataURL({
              format: 'png',
              width: width * currentZoom,
              height: height * currentZoom,
              left: (instance.getWidth() - width * currentZoom) / 2,
              top: (instance.getHeight() - height * currentZoom) / 2,
              multiplier: 3,
            });

            // Save each slide as a png, as the pdf is generated from the pngs in storage
            await CreationService.downloadSlide(generalState.creation.id, slideId, {
              content: extractBase64FromUrl(base64Url),
              format: 'png',
              dpi: 300,
              thumbnail: false,
              grayscale: false,
              email: false,
              cmyk: false,
            });

            resolve();
          });
        });
      }

      const { data: blob } = await CreationService.download(generalState.creation.id, {
        email: data.email,
        slidesId: data.slidesId,
      });

      downloadBlob(blob, `${generalState.creation.name}.pdf`);

      thunkAPI.dispatch(
        openDownloadProgressBar({
          downloadingFile: false,
          downloadDone: true,
          downloadFailed: false,
        }),
      );
    } catch (error) {
      console.error('Error downloading PDF:', error);
      thunkAPI.dispatch(
        openDownloadProgressBar({
          downloadingFile: false,
          downloadDone: false,
          downloadFailed: true,
        }),
      );
    }
  },
);

export default canvasSlice.reducer;

// function getRemainingTrialTime() {
//   const currentTime = Date.now();
//   const trialStartTime = new Date(allUserCreations[0].created).getTime();
//   const elapsedTrialTime = currentTime - trialStartTime;
//   const totalTrialTime = 25 * 60 * 1000;
//   const remainingTrialTime = totalTrialTime - elapsedTrialTime;

//   return remainingTrialTime > 0 ? remainingTrialTime : 0;
// }

export function addWatermark(obj, callback = () => {}) {
  // Check if user is trial and object type is appropriate
  if (
    !isTrial ||
    obj.type !== 'MTGImage' ||
    ![CanvasToolbarEnum.ILLUSTRATION, CanvasToolbarEnum.ICON].includes(obj.imgType)
  ) {
    callback();
    return;
  }

  const ratio =
    obj.width / obj.height < 0.6
      ? 0
      : obj.width / obj.height > 4
        ? 4
        : Math.round(obj.width / obj.height);
  const uuid =
    Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  const center = obj.getCenterPoint();

  obj.set({ uuid });

  fabric.Image.fromURL(`/images/watermark${ratio}.svg`, function (watermark) {
    if (obj.getScaledWidth() / obj.getScaledHeight() > watermark.width / watermark.height) {
      watermark.scaleToHeight(obj.height * obj.scaleY);
    } else {
      watermark.scaleToWidth(obj.width * obj.scaleX);
    }

    watermark.set({
      evented: false,
      selectable: false,
      left: center.x - watermark.getScaledWidth() / 2,
      top: center.y - watermark.getScaledHeight() / 2,
      excludeFromExport: true, // This ensures watermark is not included in exports
      watermarkTo: uuid,
      objectCaching: true, // enable caching
    });

    instance.add(watermark);
    watermark.bringForward();
    watermark.rotate(obj.angle);

    callback(watermark);
    const index = instance.getObjects().findIndex((o) => o.uuid === uuid);
    watermark.moveTo(index + 1);
  });
}

function handleDynamicShape(object) {
  instance.add(object);
  const { centerX, centerY } = placeObjectOnCanvas(object);

  object.set({
    imgType: 'dynamic_shape',
    strokeUniform: true,
    objectCaching: false,
    noScaleCache: true,
    left: centerX,
    top: centerY,
  });
  object.setCoords();
  instance.requestRenderAll();
}

function handleDynamicLine(object) {
  instance.add(object);
  const { centerX, centerY } = placeObjectOnCanvas(object);

  object.perPixelTargetFind = true;
  object.targetFindTolerance = 4;
  object.hasBorders = object.hasControls = false;
  object.left = centerX;
  object.top = centerY;

  object.setCoords();
  instance.requestRenderAll();
}

/**
 * Validates that an object exists and is of the correct type
 * @param {Object} object - The object to validate
 * @returns {boolean} Whether the object is valid
 */
function isValidObject(object) {
  if (!object || typeof object !== 'object') {
    console.warn('Invalid object provided to placeObjectOnCanvas');
    return false;
  }
  return true;
}

/**
 * Validates that an object has the required scaling methods
 * @param {Object} object - The object to validate
 * @returns {boolean} Whether the object has the required methods
 */
function hasRequiredMethods(object) {
  if (typeof object.getScaledWidth !== 'function' || typeof object.getScaledHeight !== 'function') {
    console.warn('Object is missing required scaling methods');
    return false;
  }
  return true;
}

function placeObjectOnCanvas(object) {
  try {
    if (!isValidObject(object) || !hasRequiredMethods(object)) {
      return { centerX: 0, centerY: 0 };
    }

    // Get whiteboard dimensions with fallbacks for each property
    const whiteBoardWidth = instance?.backgroundImage?.width || CANVAS_DIMENSIONS.width;
    const whiteBoardHeight = instance?.backgroundImage?.height || CANVAS_DIMENSIONS.height;
    const whiteBoardLeft = instance?.backgroundImage?.left || 0;
    const whiteBoardTop = instance?.backgroundImage?.top || 0;

    // Safely get object dimensions
    let objectWidth = 0;
    let objectHeight = 0;

    try {
      objectWidth = object.getScaledWidth();
      objectHeight = object.getScaledHeight();
    } catch (err) {
      console.warn('Error getting object dimensions:', err);
    }

    // Calculate center of whiteboard with safety checks to prevent NaN
    const centerX = whiteBoardLeft + whiteBoardWidth / 2 - (objectWidth / 2 || 0);
    const centerY = whiteBoardTop + whiteBoardHeight / 2 - (objectHeight / 2 || 0);

    return {
      centerX: isNaN(centerX) ? 0 : centerX,
      centerY: isNaN(centerY) ? 0 : centerY,
    };
  } catch (error) {
    console.error('Error in placeObjectOnCanvas:', error);
    return { centerX: 0, centerY: 0 };
  }
}

function applyBackground(width, height, fill = 'rgb(255, 255, 255)') {
  instance.setBackgroundColor('transparent');

  const overSpace = 1;
  const left = (CANVAS_DIMENSIONS.width - width) / 2 - overSpace;
  const top = (CANVAS_DIMENSIONS.height - height) / 2 - overSpace;

  const instanceBackgroundColor = instance?.backgroundImage?.src
    ? handleLegacyBackground(instance.backgroundImage.src)
    : instance?.backgroundImage?.fill;
  const fillColor = fill || instanceBackgroundColor;

  const backgroundObject = new fabric.Rect({
    originX: 'left',
    originY: 'top',
    left: left,
    top: top,
    width: width + overSpace,
    height: height + overSpace,
    fill: fillColor,
    selectable: false,
    evented: false,
    objectCaching: true,
  });

  addCanvasSizeLabel(instance, { width, height, left, top });

  if (fillColor === 'transparent') {
    backgroundObject.set({
      strokeWidth: 1,
      strokeDashArray: [2, 2],
      stroke: '#b0b0b0',
    });
  }

  instance.setBackgroundImage(backgroundObject, instance.renderAll.bind(instance));
}

function addCanvasSizeLabel(instance, { width, height, left, top }) {
  const CANVAS_SIZE_OBJECT_ID = 'canvas-size-label';
  const canvasTemplateName = getTemplateLabelBySize(width, height);
  const existingLabelObject = instance
    .getObjects()
    .find((obj) => obj.object_id === CANVAS_SIZE_OBJECT_ID);

  if (existingLabelObject) {
    instance.remove(existingLabelObject);
  }

  const label = new fabric.CustomTextLabel(`${canvasTemplateName} - ${width} x ${height} px`, {
    left: left,
    top: top - 15,
    fontSize: 12,
    fill: '#616161',
    selectable: false,
    evented: false,
    object_id: CANVAS_SIZE_OBJECT_ID,
    labelType: 'title',
    tag: 'canvas-size',
    width: width,
  });

  instance.add(label);
}

function convertLegacyObject(object, skipRepositioning = false) {
  const imgType = object.get('imgType');
  const type = object.get('type');
  const isLegacyChart = imgType === CanvasToolbarEnum.CHART;
  const isLegacyLine = type === 'line' || object.get('line');
  const isLegacyImage = ['MTGImage', 'image'].includes(type);
  const isLegacyIcon = imgType === 'icon-shape';
  const isEmptyText = type === 'i-text' && !object.get('text');

  // Remove legacy unsupported objects
  if (isLegacyChart || isEmptyText || isLegacyLine) {
    instance.remove(object);
    return;
  }

  // handle unsupported i-text object
  if (type === 'i-text') {
    object = handleLegacyIText(object);
  }

  // Update object type and properties
  if (imgType === CanvasToolbarEnum.ICON && type === 'image') {
    object.set({
      imgType: CanvasToolbarEnum.ILLUSTRATION,
      imgId: object.get('element_id'),
      hasColor: object.get('hasColor').toString(),
      imgUrl: object.get('imgSrc'),
    });
  }

  if (imgType === CanvasToolbarEnum.SHAPE) {
    object.set({
      imgId: object.get('element_id'),
      imgUrl: object.get('imgSrc'),
    });
  }

  if (imgType === CanvasToolbarEnum.SHAPE && !isLegacyImage) {
    object.set({
      imgType: CanvasToolbarEnum.DYNAMIC_SHAPE,
    });
  }

  if (isLegacyIcon) {
    object.set({
      imgType: CanvasToolbarEnum.ICON,
    });
  }

  if (imgType === 'text' || [imgType, type].includes(CanvasToolbarEnum.TEXT)) {
    object.set({
      imgType: CanvasToolbarEnum.TEXT,
    });
  }

  object.set({
    globalCompositeOperation: 'source-over',
    hasControls: true,
  });

  if (skipRepositioning) {
    return;
  }

  // Update object position
  object.set({
    top: object.get('top') + LEGACY_CANVAS_DIFF_TOP,
    left: object.get('left') + LEGACY_CANVAS_DIFF_LEFT,
  });

  object.setCoords();
}

// AD:TODO: remove this function after 2025-05-30
function updateCanvasContent(content, isCanvasEditable) {
  return new Promise((resolve) => {
    instance.loadFromJSON(updateCFBucketUrls(content), () => {
      instance.forEachObject((obj) => {
        if (obj.src && obj.src.includes('images/watermark')) {
          instance.remove(obj);
        }

        if (obj.type === 'DynamicLine') {
          obj.set({
            left: obj.left,
            top: obj.top,
            pathOffset: {
              x: 0,
              y: 0,
            },
          });
        }

        if (obj.imgType?.startsWith('comment') && !getShowCommentsFromLocalStorage()) {
          obj.set('active', false);
          obj.active = false;
          obj.visible = false;
          obj.selectable = false;
          obj.opacity = 0;
        }

        if (!isCanvasEditable) {
          obj.set({
            selectable: false,
            evented: false,
          });
        }
      });

      if (instance.getObjects().length && 1) {
        instance.forEachObject((obj) => {
          addWatermark(obj, () => {});
        });
      }

      instance.requestRenderAll();

      resolve();
    });
  });
}

function handleLegacyBackground(source) {
  if (source.includes('white')) {
    return 'rgb(255, 255, 255)';
  }

  const defaultColorsRegex = /texture|pattern|transparent/g;
  const imageColor = source.split('/').at(-1).split('.').at(0);

  const cleanImageColor = imageColor.startsWith('0') ? imageColor.slice(1) : imageColor;

  if (defaultColorsRegex.test(cleanImageColor)) {
    return 'rgb(255, 255, 255)';
  }

  return `#${cleanImageColor}`;
}

function handleLegacyIText(object) {
  const text = new fabric.Textbox(object.text, {
    left: object.left,
    top: object.top,
    fontFamily: object.fontFamily,
    fontSize: object.fontSize,
    styles: object.styles,
    width: object.width,
    height: object.height,
    scaleX: object.scaleX,
    scaleY: object.scaleY,
    objectCaching: false,
    imgType: CanvasToolbarEnum.TEXT,
  });

  instance.remove(object).add(text);
  return text;
}

// Helper function to serialize canvas with lock parameters
function serializeCanvasWithLocks(instance) {
  return {
    ...instance.toJSON(),
    objects: instance.getObjects().map((obj) => {
      const jsonObj = obj.toJSON();

      // Explicitly add lock parameters to each object
      const lockParams = [
        'lockMovementX',
        'lockMovementY',
        'lockRotation',
        'lockScalingX',
        'lockScalingY',
        'lockUniScaling',
        'lockSkewingX',
        'lockSkewingY',
      ];

      lockParams.forEach((param) => {
        if (obj[param] !== undefined) {
          jsonObj[param] = obj[param];
        }
      });

      // Explicitly preserve SVG-related properties
      if (obj.originalSvgId !== undefined) {
        jsonObj.originalSvgId = obj.originalSvgId;
      }

      return jsonObj;
    }),
  };
}

// Snapshot helper dedicated to saving slides.
// Ensures control points are dropped while lock and SVG fields survive serialization
// so the backend can faithfully rebuild editing constraints and regroup metadata.
function serializeCanvasForSave(instance) {
  // Lock parameters that fabric.js uses
  const lockParams = [
    'lockMovementX',
    'lockMovementY',
    'lockRotation',
    'lockScalingX',
    'lockScalingY',
    'lockUniScaling',
    'lockSkewingX',
    'lockSkewingY',
  ];

  // SVG-related properties for regroup functionality
  const svgParams = ['originalSvgId', 'imgType'];

  // Get base serialization without extra props (custom classes may not support them)
  const baseJSON = instance.toJSON();
  const sourceObjects = instance.getObjects();

  return {
    ...baseJSON,
    // Manually copy lock params and SVG metadata from live objects, filter out control points
    objects:
      baseJSON.objects
        ?.map((jsonObj, i) => {
          const obj = sourceObjects[i];
          if (!obj || jsonObj.controlPoint) return null;

          // Build object with lock params and SVG metadata from live canvas object
          const metadata = {};
          [...lockParams, ...svgParams].forEach((key) => {
            if (obj[key] !== undefined) {
              metadata[key] = obj[key];
            }
          });

          return { ...jsonObj, ...metadata };
        })
        .filter(Boolean) || [],
  };
}
